import * as React from 'react';
import { WhiteLabelMobileResourceTile, SearchBox, RegExpValidator, Magnetic } from '@mitchell/react.component.ux';
import { TranslationService, getZipRegexAndMessageByCountryCode, CountryCode, getLatitudeLongitude, EMPTY_TEXT } from "../../common";
import { ResourceLookUpConstants } from '../constants';
import { StyledRow, StyledNoRecordDiv, StyledZipValidationDiv, StyledSearchBox, StyledResourceListContainer } from './RepairResourceLookupComponent.style';
/**
 * @component RepairResourceLookupComponent
 * @param resourcesData {Array<IResourceInfo>} represent the data of repair facilities
 * @param isResourceFailure {boolean} represent the check resource loading failure
 * @param repairResourceLookupPayload {IRepairResourceLookupPayload} payload to be passed to repair resource service
 * @method 	getRepairResourceInfo represents the get dispatch action
 * @method onResourceItemClicked [optional] represents the callback function when resource item will click
 * @param selectedResourceId {string} [optional] represent the selected resource item id
 * @param selectedButtonBackgroundColor {string} [optional] represents the selected background color of resource item
 */
export const RepairResourceLookupComponent = ({ resourcesData, isResourceFailure, getRepairResourceInfo, repairResourceLookupPayload, onResourceItemClicked, selectedResourceId, selectedButtonBackgroundColor, isResourcesLoadingSuccess, assignedBodyShopDetails, noShopsFoundMessage }) => {
    const translateFn = TranslationService.translate;
    const [isPostalCodeValid, setIsPostalCodeValid] = React.useState(true);
    const [resourceShopList, setResourceShopList] = React.useState(resourcesData);
    const [postalCode, setPostalCode] = React.useState(EMPTY_TEXT);
    const [isAssignedShopExist, setIsAssignedShopExist] = React.useState(false);
    const zipValidationInfo = getZipRegexAndMessageByCountryCode(repairResourceLookupPayload.countryCode);
    React.useEffect(() => {
        if (!postalCode && assignedBodyShopDetails) {
            // Check if resourceShopList already contains the body shop
            if (resourcesData.length) {
                // Check if the body shop already exists in the list by orgCd
                const existingShopIndex = resourcesData.findIndex((item) => item.orgCd === assignedBodyShopDetails.orgCd);
                // If the shop exists, remove it and add the new one at the front
                if (existingShopIndex !== -1) {
                    const reorderedUsers = [
                        resourcesData[existingShopIndex],
                        ...resourcesData.filter((_, i) => i !== existingShopIndex)
                    ];
                    setResourceShopList(reorderedUsers); // Add the new shop at the beginning
                }
                else {
                    // If not a duplicate, just add the new shop at the front
                    setResourceShopList([assignedBodyShopDetails, ...resourcesData]);
                }
            }
            else {
                // If the list is empty, just add the body shop as the first element
                setResourceShopList([assignedBodyShopDetails]);
            }
        }
        else {
            // If postalCode exists, just reset the list with resourcesData
            setResourceShopList(resourcesData);
        }
    }, [resourcesData]);
    React.useEffect(() => {
        if (postalCode && isPostalCodeValid) {
            getRepairResourceInfo(Object.assign(Object.assign({}, repairResourceLookupPayload), { postalCode }));
            setIsAssignedShopExist(false);
        }
        else {
            const geolocation = getLatitudeLongitude();
            geolocation.then(({ latitude, longitude }) => {
                repairResourceLookupPayload.lat = latitude;
                repairResourceLookupPayload.long = longitude;
                latitude && longitude && getRepairResourceInfo(repairResourceLookupPayload);
            });
            setIsAssignedShopExist(true);
        }
    }, [postalCode]);
    const onItemClicked = (selectedId) => () => {
        onResourceItemClicked && onResourceItemClicked(selectedId);
    };
    const distanceUnit = repairResourceLookupPayload.countryCode === CountryCode.UNITED_STATES
        ? ResourceLookUpConstants.MILES
        : ResourceLookUpConstants.KILOMETER;
    const onSearch = (postalCode) => {
        let isValidUpdatedPostalCode = false;
        if (postalCode === '')
            isValidUpdatedPostalCode = true;
        else
            isValidUpdatedPostalCode = RegExpValidator.isValid(zipValidationInfo.setZipRegex, postalCode);
        isValidUpdatedPostalCode && setPostalCode(postalCode);
        setIsPostalCodeValid(isValidUpdatedPostalCode);
    };
    const onCancelIconClick = () => setIsPostalCodeValid(true);
    const cancelButtonProps = { shouldHide: true };
    const defaultSearchInputProps = {
        id: `${ResourceLookUpConstants.MODULE_PREFIX}_search-input`,
        onChange: () => { },
        placeholder: translateFn(ResourceLookUpConstants.SEARCHBY_ZIP_POSTALCODE)
    };
    return (React.createElement("div", null,
        React.createElement(StyledSearchBox, null,
            React.createElement(SearchBox, { onSearch: onSearch, cancelButtonProps: cancelButtonProps, searchInputProps: defaultSearchInputProps, shouldShowCrossIcon: true, onCancelIconClick: onCancelIconClick }),
            !isPostalCodeValid && (React.createElement(StyledZipValidationDiv, null, translateFn(zipValidationInfo.setZipValidationMessage)))),
        (isResourceFailure || isResourcesLoadingSuccess) && resourceShopList && !resourceShopList.length && (React.createElement(StyledNoRecordDiv, null, translateFn(noShopsFoundMessage || ResourceLookUpConstants.NO_RECORD_FOUND))),
        React.createElement(StyledResourceListContainer, null, resourceShopList &&
            !!resourceShopList.length &&
            resourceShopList.map((data, index) => {
                const isAssignedShop = !!(isAssignedShopExist && assignedBodyShopDetails && index === 0);
                const isSelected = selectedResourceId === data.orgCd || isAssignedShop;
                const highlightBackgroundColor = isAssignedShop ? Magnetic.colorYellow07 : EMPTY_TEXT;
                const highlightText = isAssignedShop ? ResourceLookUpConstants.HIGHLIGHT_TEXT : EMPTY_TEXT;
                const distance = distanceUnit === ResourceLookUpConstants.MILES
                    ? parseFloat(data.distance)
                    : Math.round(parseFloat(data.distance) * ResourceLookUpConstants.CONVERT_MILES_TO_KM);
                return (React.createElement(StyledRow, { key: `${index}${data.resourceName}` },
                    React.createElement(WhiteLabelMobileResourceTile, { tileId: `${data.orgCd}${data.resourceName}`, heading: data.resourceName || EMPTY_TEXT, subHeading: `${data.addressLine1}<br />${data.city} ${data.state} <br />${data.coCd}, ${data.postalCd}`, distance: distance, distanceUnit: distanceUnit, onItemClicked: onItemClicked(data.orgCd), backgroundColor: Magnetic.colorWhite, selectedBackgroundColor: selectedButtonBackgroundColor, isItemSelected: isSelected, highLightBackgroundColor: highlightBackgroundColor, highLightText: translateFn(highlightText) })));
            }))));
};
//# sourceMappingURL=RepairResourceLookupComponent.js.map